import { Navigate } from 'react-router-dom'

import async from '@/components/Async'
// Guards
import AuthGuard from '@/components/guards/AuthGuard'
import GuestGuard from '@/components/guards/GuestGuard'
import { DAIPermissionCodename } from '@/graphql'
import useAuth from '@/hooks/useAuth'
// All pages that rely on 3rd party components (other than MUI) are
// loaded asynchronously, to keep the initial JS bundle to a minimum size
// Layouts
import AccountSetUpLayout from '@/layouts/AccountSetUpLayout'
import AuthLayout from '@/layouts/Auth'
import DashboardLayout from '@/layouts/Dashboard'
import ErrorLayout from '@/layouts/Error'
// Account Set Up components
import AccountSetUp from '@/pages/account-set-up/AccountSetUp'
// Auth components
import AcceptInvite from '@/pages/auth/AcceptInvite'
import AuthActionHandler from '@/pages/auth/AuthActionHandler'
import Page404 from '@/pages/auth/Page404'
import Page500 from '@/pages/auth/Page500'
import SignIn from '@/pages/auth/SignIn'
import SignUp from '@/pages/auth/SignUp'

import PaymentGuard from './components/guards/PaymentGuard'
import PermissionGuard from './components/guards/PermissionGuard'
import SingleStoreGuard from './components/guards/SingleStoreGuard'
import PricingLayout from './layouts/PricingLayout'
import SnapshotCompliance from './pages/compliance/SnapshotCompliance'
import CoolerDetail from './pages/coolers/CoolerDetail'
// Dashboard components
import Home from './pages/dashboards/Default/Home'
import { EmptyFacingsSnapshot } from './pages/empty-facings/EmptyFacingsSnapshot'
import PlanManagementAndBilling from './pages/pages/billing/PlanManagementAndBilling'
import { NotAuthorized } from './pages/pages/NotAuthorized'
import Pricing from './pages/pages/Pricing'
import PrivacyPolicy from './pages/pages/PrivacyPolicy'
import ProductDetails from './pages/pages/ProductDetails'
import Restocks from './pages/pages/Restocks'
import StoreAssignments from './pages/pages/StoreAssignments'
import Users from './pages/pages/Users'
import EditPlanogram from './pages/planogram/components/EditPlanogram'
import PlanogramList from './pages/planogram/components/PlanogramList'
import RestockGroupDetail from './pages/restocks/restock-group/RestockGroupDetail'

// Admin Store components
const RestockSetupPage = async(() => import('./pages/pages/RestockSetupPage'))
const TaskPage = async(() => import('./pages/pages/Task'))
const ActionAiContextImages = async(
  () => import('./pages/pages/ActionAiContextImages'),
)
const ActionAiImageFeedback = async(
  () => import('./pages/pages/ActionAiImageFeedback'),
)
const Stores = async(() => import('./pages/dashboards/Stores'))
const Tasks = async(() => import('./pages/pages/Tasks'))
const EditUser = async(() => import('@/pages/pages/EditUser'))
const StoreDetails = async(() => import('./pages/pages/StoreDetails'))
const TaskList = async(() => import('./pages/pages/TaskList'))
const Profile = async(() => import('@/pages/pages/Profile'))
const ProductsCategories = async(
  () => import('./pages/pages/ProductsCategories'),
)
const AdminSalesData = async(() => import('@/pages/pages/AdminSalesData'))
const RestockStoresReport = async(
  () => import('./pages/restocks/ActionGeneratorStoresReport'),
)
const Revenue = async(() => import('./pages/revenue/Revenue'))
const EmptyFacings = async(() => import('./pages/empty-facings/EmptyFacings'))
const Compliance = async(() => import('./pages/compliance/Compliance'))
const DisplayCompliance = async(
  () => import('./pages/compliance/DisplayCompliance'),
)

const AuthRedirect = () => {
  const { isAuthenticated } = useAuth()
  return isAuthenticated ? (
    <Navigate to="/home" replace />
  ) : (
    <Navigate to="/auth/sign-in" replace />
  )
}

const authRoutes = [
  {
    path: 'auth',
    element: <AuthLayout />,
    children: [
      {
        path: 'sign-in',
        element: (
          <GuestGuard>
            <SignIn />
          </GuestGuard>
        ),
      },
      {
        path: 'sign-up',
        element: (
          <GuestGuard>
            <SignUp />
          </GuestGuard>
        ),
      },
      {
        path: 'accept-invite',
        element: <AcceptInvite />,
      },
      {
        path: 'action',
        element: <AuthActionHandler />,
      },
    ],
  },
  {
    path: 'privacy-policy',
    element: <PrivacyPolicy />,
  },
]

const storeRoutes = [
  {
    path: 'stores',
    element: (
      <AuthGuard>
        <PermissionGuard
          requiredPermissions={[DAIPermissionCodename.CAN_LOGIN_TO_WEB_APP]}
        >
          <DashboardLayout />
        </PermissionGuard>
      </AuthGuard>
    ),
    children: [
      { path: '', element: <Stores /> },
      {
        path: ':id',
        element: <StoreDetails />,
      },
      {
        path: ':id/tasks',
        children: [
          {
            path: '',
            element: <Tasks />,
          },
          {
            path: 'management',
            element: <TaskList />,
          },
        ],
      },
      {
        path: ':id/restocks',
        children: [
          {
            path: '',
            element: <Restocks />,
          },
          {
            path: 'management',
            element: <RestockSetupPage />,
          },
          {
            path: ':actionGeneratorId',
            children: [
              {
                path: '',
                element: <RestockStoresReport />,
              },
              {
                path: ':restockGroupId',
                element: <RestockGroupDetail />,
              },
            ],
          },
        ],
      },
      {
        path: ':id/revenue',
        element: <Revenue />,
      },
      {
        path: ':id/empty-facings',
        children: [
          {
            path: '',
            element: <EmptyFacings />,
          },
          {
            path: ':snapshotId',
            element: <EmptyFacingsSnapshot />,
          },
        ],
      },
      {
        path: ':id/planograms',
        children: [
          {
            path: '',
            element: <PlanogramList />,
          },
          {
            path: ':displayId',
            element: <EditPlanogram />,
          },
        ],
      },
      {
        path: ':id/compliance',
        children: [
          {
            path: '',
            element: <Compliance />,
          },
          {
            path: ':snapshotId',
            element: <SnapshotCompliance />,
          },
        ],
      },
      {
        path: ':id/displays/:displayId',
        element: <CoolerDetail />,
      },
      {
        path: ':id/displays/:displayId/compliance',
        element: <DisplayCompliance />,
      },
      {
        path: ':id/displays/:displayId/compliance/:snapshotId',
        element: <SnapshotCompliance />,
      },
    ],
  },
]

const routes = [
  {
    path: '/',
    element: <AuthRedirect />,
  },
  {
    path: '/not-authorized',
    element: <NotAuthorized />,
  },
  {
    path: 'account-set-up',
    element: (
      <AuthGuard>
        <AccountSetUpLayout />
      </AuthGuard>
    ),
    children: [{ path: '', element: <AccountSetUp /> }],
  },
  {
    path: 'pricing',
    element: (
      <AuthGuard>
        <PermissionGuard
          requiredPermissions={[DAIPermissionCodename.CAN_LOGIN_TO_WEB_APP]}
        >
          <PricingLayout />
        </PermissionGuard>
      </AuthGuard>
    ),
    children: [{ path: '', element: <Pricing /> }],
  },
  ...authRoutes,
  ...storeRoutes,
  {
    path: '',
    element: (
      <AuthGuard>
        <PermissionGuard
          navigateTo="/account-set-up?step=congrats"
          requiredPermissions={[DAIPermissionCodename.CAN_LOGIN_TO_WEB_APP]}
        >
          <SingleStoreGuard>
            <DashboardLayout />
          </SingleStoreGuard>
        </PermissionGuard>
      </AuthGuard>
    ),
    children: [
      {
        path: 'home',
        element: <Home />,
      },
      { path: 'home/revenue', element: <Revenue /> },
      { path: 'home/empty-facings', element: <EmptyFacings /> },
      { path: 'home/compliance', element: <Compliance /> },
      {
        path: 'home/compliance/:snapshotId',
        element: <SnapshotCompliance />,
      },
      {
        path: 'home/empty-facings/:snapshotId',
        element: <EmptyFacingsSnapshot />,
      },
    ],
  },
  {
    path: 'admin',
    element: (
      <AuthGuard>
        <PermissionGuard
          requiredPermissions={[DAIPermissionCodename.CAN_LOGIN_TO_WEB_APP]}
        >
          <DashboardLayout />
        </PermissionGuard>
      </AuthGuard>
    ),
    children: [
      {
        path: 'profile',
        element: <Profile />,
      },
      {
        path: 'users',
        element: <Users />,
      },
      {
        path: 'plan-management',
        children: [
          {
            path: '',
            element: (
              <PaymentGuard>
                <PlanManagementAndBilling />
              </PaymentGuard>
            ),
          },
        ],
      },
      {
        path: 'store-assignments',
        element: <StoreAssignments />,
      },
      {
        path: 'sales-data',
        element: <AdminSalesData />,
      },
      {
        path: 'users/edit-user',
        element: <EditUser />,
      },
      {
        // Redirect from /pages/settings to /pages/settings/users
        path: '',
        element: <Navigate to="users" replace />,
      },
    ],
  },
  {
    path: 'products',
    element: (
      <AuthGuard>
        <PermissionGuard
          requiredPermissions={[DAIPermissionCodename.CAN_LOGIN_TO_WEB_APP]}
        >
          <DashboardLayout />
        </PermissionGuard>
      </AuthGuard>
    ),
    children: [
      {
        path: '',
        element: <ProductsCategories />,
      },
    ],
  },
  {
    path: 'products/:id',
    element: (
      <AuthGuard>
        <PermissionGuard
          requiredPermissions={[DAIPermissionCodename.CAN_LOGIN_TO_WEB_APP]}
        >
          <DashboardLayout />
        </PermissionGuard>
      </AuthGuard>
    ),
    children: [
      {
        path: '',
        element: <ProductDetails />,
      },
    ],
  },
  {
    path: 'tasks',
    element: (
      <AuthGuard>
        <PermissionGuard
          requiredPermissions={[DAIPermissionCodename.CAN_LOGIN_TO_WEB_APP]}
        >
          <DashboardLayout />
        </PermissionGuard>
      </AuthGuard>
    ),
    children: [
      {
        path: '',
        element: <Tasks />,
      },
      {
        path: 'management',
        element: (
          <PermissionGuard
            requiredPermissions={[DAIPermissionCodename.CAN_EDIT_SYSTEM_TASKS]}
          >
            <TaskList />
          </PermissionGuard>
        ),
      },
      {
        path: ':id',
        element: <TaskPage />,
      },
      {
        path: ':generatorId/context-images',
        element: <ActionAiContextImages />,
      },
      {
        path: ':generatorId/image-feedback',
        element: <ActionAiImageFeedback />,
      },
    ],
  },
  {
    path: 'restocks',
    element: (
      <AuthGuard>
        <PermissionGuard
          requiredPermissions={[DAIPermissionCodename.CAN_LOGIN_TO_WEB_APP]}
        >
          <DashboardLayout />
        </PermissionGuard>
      </AuthGuard>
    ),
    children: [
      {
        path: '',
        element: <Restocks />,
      },
      { path: 'management', element: <RestockSetupPage /> },
    ],
  },
  {
    path: 'error',
    element: <ErrorLayout />,
    children: [
      {
        path: '404',
        element: <Page404 />,
      },
      {
        path: '500',
        element: <Page500 />,
      },
      {
        path: 'not-authorized',
        element: <NotAuthorized />,
      },
    ],
  },
  {
    path: '*',
    element: <ErrorLayout />,
    children: [
      {
        path: '*',
        element: <Page404 />,
      },
    ],
  },
]

export default routes
